// Tiny fetch wrapper. The frontend always uses relative URLs - the same Pages
// origin serves both the SPA and the /api/v1/* Functions.

import type {
  ConnectorDetail,
  ConnectorListItem,
  ConnectorTypeInfo,
  GenerateRequest,
  TemplateRow,
} from './types';

async function request<T>(input: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(input, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const j = JSON.parse(text);
      msg = j.error || text;
    } catch {}
    throw new Error(`${res.status} ${res.statusText}: ${msg}`);
  }
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export const api = {
  health: () => request<{ status: string; service: string; environment: string }>('/api/v1/health'),
  listConnectorTypes: () => request<ConnectorTypeInfo[]>('/api/v1/connector-types'),

  // Templates
  listTemplates: () => request<TemplateRow[]>('/api/v1/templates'),
  getTemplate: (id: string) => request<TemplateRow & { has_base_pdf?: boolean }>(`/api/v1/templates/${id}`),
  // pdfme's Template.basePdf only accepts http(s) URLs pointing to public hosts.
  // Return the public URL of our cached /basepdf endpoint and let pdfme fetch it.
  getTemplateBasePdfUrl: (id: string): string =>
    `${location.origin}/api/v1/templates/${id}/basepdf`,
  getTemplateBasePdf: async (id: string): Promise<string> => {
    // legacy fallback: returns a `data:application/pdf;base64,…` URL which pdfme's
    // URL validator rejects on modern Chrome. Prefer getTemplateBasePdfUrl().
    const res = await fetch(`/api/v1/templates/${id}/basepdf`);
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    const buf = await res.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return `data:application/pdf;base64,${btoa(binary)}`;
  },
  createTemplate: (body: { name: string; description?: string; template_json: string; base_pdf?: string; font_json?: string }) =>
    request<TemplateRow>('/api/v1/templates', { method: 'POST', body: JSON.stringify(body) }),
  updateTemplate: (id: string, body: Partial<TemplateRow>) =>
    request<TemplateRow>(`/api/v1/templates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteTemplate: (id: string) =>
    request<{ deleted: string }>(`/api/v1/templates/${id}`, { method: 'DELETE' }),

  // Connectors
  listConnectors: () => request<ConnectorListItem[]>('/api/v1/connectors'),
  getConnector: (id: string) => request<ConnectorDetail>(`/api/v1/connectors/${id}`),
  createConnector: (body: { name: string; type: string; config: any }) =>
    request<ConnectorDetail>('/api/v1/connectors', { method: 'POST', body: JSON.stringify(body) }),
  updateConnector: (id: string, body: { name?: string; config?: any; is_active?: boolean }) =>
    request<ConnectorDetail>(`/api/v1/connectors/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteConnector: (id: string) =>
    request<{ deleted: string }>(`/api/v1/connectors/${id}`, { method: 'DELETE' }),
  testConnector: (id: string) =>
    request<{ ok: boolean; message: string; details?: any }>(`/api/v1/connectors/${id}/test`, { method: 'POST' }),
  rotateApiKey: (id: string) =>
    request<{ id: string; api_key: string }>(`/api/v1/connectors/${id}/rotate-key`, { method: 'POST' }),
  rotateWebhookSecret: (id: string) =>
    request<{ id: string; webhook_secret: string }>(`/api/v1/connectors/${id}/rotate-secret`, { method: 'POST' }),

  // Logs
  listGenerations: (limit = 50, offset = 0) =>
    request<any[]>(`/api/v1/generations?limit=${limit}&offset=${offset}`),
  generationStats: () => request<{ total: number; success: number; failed: number; last_24h: number }>('/api/v1/generations/stats'),
};

// File generation (returns a Blob, not JSON).
export async function generatePdf(
  body: GenerateRequest,
  apiKey?: string
): Promise<{ blob: Blob; generationId: string | null }> {
  const headers: Record<string, string> = {};
  if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;
  const res = await fetch('/api/v1/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return {
    blob: await res.blob(),
    generationId: res.headers.get('X-Generation-Id'),
  };
}
